import { useState } from "react"
import { RecurringDonation, Designation } from "../types"
import { Badge, Heading } from "@planningcenter/doxy-web"
import { formattedCents } from "source/giving/utils/money"
import { PaymentMethodIcon } from "source/payments/components/icons/PaymentMethodIcon"
import { AlertMessage, Icon } from "source/shared/components"
import { date } from "@churchcenter/datetime-fmt"
import EditRecurringDonationForm from "./EditRecurringDonationForm"
import { css } from "@emotion/react"
import { Link } from "react-router-dom"
import { usePaymentMethodsContext } from "../hooks/usePaymentMethodsContext"

interface RecurringDonationDetailsProps {
  donation: RecurringDonation
  showFullDetails: boolean
  onEdit: (id: string | null) => void
  isDisabled: boolean
}

export default function RecurringDonationDetails({
  donation,
  showFullDetails,
  onEdit,
  isDisabled,
}: RecurringDonationDetailsProps) {
  const { paymentMethods } = usePaymentMethodsContext()

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const paymentMethod =
    paymentMethods.find(
      (paymentMethod) => paymentMethod.id === donation.payment_method_id,
    ) || null

  const designationTotal = (designations: Designation[]): number => {
    const totalCents = designations.reduce((totalCents, designation) => {
      const amountInCents =
        Math.round(parseFloat(designation.amount) * 100) || 0
      return totalCents + amountInCents
    }, 0)

    return totalCents
  }

  const donationTotalAmount = designationTotal(donation.designations)

  const handleEditClick = () => {
    setIsEditing(true)
    onEdit(donation.id)
  }

  const handleCancel = () => {
    setIsEditing(false)
    onEdit(null)
  }

  const nextOccurrenceDate = donation.next_occurrence
    ? (() => {
        const nextDate = new Date(donation.next_occurrence)
        const isCurrentYear =
          nextDate.getFullYear() === new Date().getFullYear()
        return date(donation.next_occurrence, {
          style: "standard",
          year: !isCurrentYear,
        })
      })()
    : null

  const recurringDonationHeadingText = `${formattedCents(donationTotalAmount)}${nextOccurrenceDate && !donation.on_hold_indefinitely ? ` on ${nextOccurrenceDate}` : ""}`

  const isOnHold =
    donation.on_hold_indefinitely ||
    (donation.release_hold_at &&
      new Date(donation.release_hold_at) > new Date())

  return (
    <div className="d-f fd-c g-2">
      <div
        className="d-f fd-c g-2 action-drawer"
        css={css`
          opacity: ${isDisabled ? "0.5" : "1"};
          background-color: ${isDisabled
            ? "var(--color-tint7)"
            : "var(--color-tint9)"};
        `}
      >
        {isEditing ? (
          <EditRecurringDonationForm
            recurringDonation={donation}
            donationTotalAmount={donationTotalAmount}
            onCancel={handleCancel}
          />
        ) : (
          <>
            <div className="d-f ai-fs jc-sb">
              <div className="d-f fd-c g-4p">
                <div className="d-f ai-c g-1 fw-w">
                  <Heading
                    level={3}
                    size={4}
                    text={recurringDonationHeadingText}
                    color="tint1"
                  />
                  {isOnHold && <Badge theme="warning" text="On hold" />}
                  {donation.awaiting_verification && (
                    <Badge theme="warning" text="Awaiting verification" />
                  )}
                  {!showFullDetails && !paymentMethod && (
                    <Badge theme="error" text="Payment method missing" />
                  )}
                </div>
                <div className="fs-13 fw-600 c-tint2 tt-u">
                  {donation.humanized_schedule}
                </div>
              </div>
              <button
                type="button"
                className="clickable-icon text-btn p-4p"
                onClick={handleEditClick}
                disabled={isDisabled}
                aria-label="Edit this donation"
              >
                <Icon
                  symbol="general#pencil"
                  title="pencil"
                  aria-hidden
                  css={css`
                    height: 12px;
                    width: 12px;
                  `}
                />
              </button>
            </div>
            <div className={`d-f g-1 ${showFullDetails ? "fd-c" : "fw-w"}`}>
              {donation.designations.map((designation) => {
                return (
                  <div className="d-f g-1 truncate" key={designation.id}>
                    <span className="d-b badge truncate">
                      {designation.fund.name}
                    </span>
                    {showFullDetails && (
                      <span>
                        {formattedCents(parseFloat(designation.amount) * 100)}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            {showFullDetails &&
              (!paymentMethod ? (
                <AlertMessage
                  type="error"
                  icon="general#exclamation-triangle"
                  className="mb-0"
                >
                  Payment method missing
                </AlertMessage>
              ) : (
                <div className="d-f ai-c">
                  <PaymentMethodIcon
                    brand={paymentMethod.brand}
                    isCard={paymentMethod.method_type === "card"}
                  />
                  <span className="payment-method-badge__meta fs-4">
                    {paymentMethod.brand ?? ""}{" "}
                    {paymentMethod.last4 ? `...${paymentMethod.last4}` : ""}
                  </span>
                </div>
              ))}
            {donation.awaiting_verification && (
              <Link to="/me/profile/payment-methods" className="fs-13 fw-500">
                Verify bank account
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  )
}
